import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, MapPin } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('tr') ? 'tr' : 'en';
  const [hfContent, setHfContent] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/public/content/header_footer`).then(res => {
      if (res.data && Object.keys(res.data).length > 0) setHfContent(res.data);
    }).catch(() => {});
  }, []);

  // If admin has saved content (hfContent exists), use admin values even if empty.
  // Only fall back to defaults when no admin content has been saved at all.
  const hasContent = hfContent !== null;

  const getText = (field, fallback) => {
    if (field === undefined || field === null) return hasContent ? '' : (fallback || '');
    if (typeof field === 'object') return field[lang] ?? field.en ?? '';
    return field;
  };

  const footerLogo = (hasContent ? hfContent?.footer_logo : null) ?? "/viyero-logo.webp";
  const footerEmail = (hasContent ? hfContent?.footer_email : null) ?? 'info@viyero.com';
  const footerAddress = getText(hfContent?.footer_address, t('footer.address'));
  const footerDesc = getText(hfContent?.footer_description, t('footer.description'));
  const productsTitle = getText(hfContent?.footer_products_title, t('footer.productsTitle'));
  const companyTitle = getText(hfContent?.footer_company_title, t('footer.companyTitle'));

  const gamingLink = getText(hfContent?.footer_gaming_link, t('footer.gamingHardware'));
  const officeLink = getText(hfContent?.footer_office_link, t('footer.officeHome'));
  const aboutLink = getText(hfContent?.footer_about_link, t('footer.about'));
  const contactLink = getText(hfContent?.footer_contact_link, t('footer.contact'));

  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'instant' });

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12">
          <div className="col-span-1 md:col-span-2">
            <img src={footerLogo} alt="VIYERO" className="h-8 md:h-10 w-auto mb-4" />
            <p className="text-gray-400 mb-6 max-w-md">{footerDesc}</p>
          </div>

          <div>
            {productsTitle && <h4 className="text-white font-semibold mb-4">{productsTitle}</h4>}
            <ul className="space-y-2">
              {gamingLink && <li><Link to="/gaming" onClick={scrollTop} className="text-gray-400 hover:text-white transition-colors">{gamingLink}</Link></li>}
              {officeLink && <li><Link to="/office" onClick={scrollTop} className="text-gray-400 hover:text-white transition-colors">{officeLink}</Link></li>}
            </ul>
          </div>

          <div>
            {companyTitle && <h4 className="text-white font-semibold mb-4">{companyTitle}</h4>}
            <ul className="space-y-2">
              {aboutLink && <li><Link to="/about" onClick={scrollTop} className="text-gray-400 hover:text-white transition-colors">{aboutLink}</Link></li>}
              {contactLink && <li><Link to="/contact" onClick={scrollTop} className="text-gray-400 hover:text-white transition-colors">{contactLink}</Link></li>}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
              {footerEmail && (
              <a href={`mailto:${footerEmail}`} className="flex items-center gap-2 hover:text-white transition-colors">
                <Mail className="h-4 w-4" />
                <span className="text-sm">{footerEmail}</span>
              </a>
              )}
              {footerAddress && (
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                <span className="text-sm">{footerAddress}</span>
              </div>
              )}
            </div>
            <p className="text-sm text-gray-500">
              &copy; {new Date().getFullYear()} VIYERO. {t('footer.rights')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
